// ─── Credentia Deadline Utilities ────────────────────────────────────────────

export const RENEWAL_LINKS = {
  'ALS': {
    label: 'Book ALS renewal — Resuscitation Council UK',
    url: 'https://www.resus.org.uk/training-and-resources/als-course-finder',
  },
  'APLS': {
    label: 'Book APLS renewal — ALSG',
    url: 'https://www.alsg.org/courses/apls',
  },
  'ATLS': {
    label: 'Book ATLS renewal — RCS England',
    url: 'https://www.rcseng.ac.uk/education-and-exams/courses/atls',
  },
  'Safeguarding Children': {
    label: 'NHS England safeguarding e-learning',
    url: 'https://www.e-lfh.org.uk/programmes/safeguarding-children',
  },
  'Safeguarding Adults': {
    label: 'NHS England safeguarding e-learning',
    url: 'https://www.e-lfh.org.uk/programmes/safeguarding-adults',
  },
  'DBS': {
    label: 'Apply for DBS check — GOV.UK',
    url: 'https://www.gov.uk/request-copy-criminal-record',
  },
  'GMC Registration': {
    label: 'Renew GMC registration',
    url: 'https://www.gmc-uk.org/registration-and-licensing/renew-your-registration',
  },
  'GCP': {
    label: 'NIHR GCP e-learning',
    url: 'https://www.nihr.ac.uk/health-and-care-professionals/learning-and-support/good-clinical-practice.htm',
  },
};

// Returns the first matching renewal link for a given item label
export function findRenewalLink(itemLabel) {
  if (!itemLabel) return null;
  const lower = itemLabel.toLowerCase();
  for (const [key, val] of Object.entries(RENEWAL_LINKS)) {
    if (lower.includes(key.toLowerCase())) return val;
  }
  return null;
}

/**
 * Returns urgency info for an expiry date string (YYYY-MM-DD).
 * level: 'expired' | 'critical' | 'urgent' | 'soon' | 'ok'
 */
export function getUrgency(expiryDateStr) {
  if (!expiryDateStr) return null;
  const expiry = new Date(expiryDateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntil = Math.floor((expiry - now) / msPerDay);
  const weeksUntil = Math.floor(daysUntil / 7);
  const monthsUntil = Math.floor(daysUntil / 30);

  if (daysUntil < 0) {
    return {
      level: 'expired',
      color: '#ef4444',
      barColor: '#ef4444',
      bgColor: 'rgba(239,68,68,.08)',
      borderColor: 'rgba(239,68,68,.25)',
      label: 'EXPIRED — renew immediately before submission',
      shortLabel: 'Expired',
      daysUntil,
      weeksUntil,
      monthsUntil,
    };
  }
  if (daysUntil <= 14) {
    return {
      level: 'critical',
      color: '#ef4444',
      barColor: '#ef4444',
      bgColor: 'rgba(239,68,68,.08)',
      borderColor: 'rgba(239,68,68,.25)',
      label: `Expires in ${daysUntil} day${daysUntil === 1 ? '' : 's'} — urgent`,
      shortLabel: `${daysUntil}d`,
      daysUntil,
      weeksUntil,
      monthsUntil,
    };
  }
  if (daysUntil <= 42) {
    return {
      level: 'urgent',
      color: '#f59e0b',
      barColor: '#f59e0b',
      bgColor: 'rgba(245,158,11,.08)',
      borderColor: 'rgba(245,158,11,.25)',
      label: `Expires in ${weeksUntil} week${weeksUntil === 1 ? '' : 's'} — book renewal now`,
      shortLabel: `${weeksUntil}w`,
      daysUntil,
      weeksUntil,
      monthsUntil,
    };
  }
  if (daysUntil <= 84) {
    return {
      level: 'soon',
      color: '#eab308',
      barColor: '#eab308',
      bgColor: 'rgba(234,179,8,.08)',
      borderColor: 'rgba(234,179,8,.25)',
      label: `Expires in ${weeksUntil} week${weeksUntil === 1 ? '' : 's'} — plan ahead`,
      shortLabel: `${weeksUntil}w`,
      daysUntil,
      weeksUntil,
      monthsUntil,
    };
  }
  return {
    level: 'ok',
    color: '#10b981',
    barColor: '#10b981',
    bgColor: 'rgba(16,185,129,.06)',
    borderColor: 'rgba(16,185,129,.2)',
    label: `Expires in ${monthsUntil} month${monthsUntil === 1 ? '' : 's'} — on track`,
    shortLabel: `${monthsUntil}mo`,
    daysUntil,
    weeksUntil,
    monthsUntil,
  };
}

/**
 * Compute reminder dates from an expiry date string.
 * If a reminder date is already in the past, marks it as sent so it never fires.
 */
export function computeReminderDates(expiryDateStr) {
  if (!expiryDateStr) return {};
  const expiry = new Date(expiryDateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const subtractDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() - days);
    return d;
  };
  const toDateStr = (d) => d.toISOString().split('T')[0];

  const r12 = subtractDays(expiry, 84);
  const r6  = subtractDays(expiry, 42);
  const r2  = subtractDays(expiry, 14);

  return {
    reminder_12w_date: toDateStr(r12),
    reminder_6w_date:  toDateStr(r6),
    reminder_2w_date:  toDateStr(r2),
    // Mark as already sent if the date is already past — never trigger stale reminders
    reminder_12w_sent: r12 < today,
    reminder_6w_sent:  r6  < today,
    reminder_2w_sent:  r2  < today,
  };
}

/** Format a YYYY-MM-DD date as "14 March 2025" */
export function formatExpiryDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
